import React from 'react';
import { useTranslation } from 'react-i18next';

import { PIX_BRAND } from '../../pix.tokens';
import { LogoWrap } from './PixLogo.styles';

/**
 * The Pix mark: the diamond-link glyph plus the "pix" wordmark, in the brand teal and on no
 * background.
 *
 * No official vector was supplied, so the glyph is drawn here as a linked diamond and the
 * wordmark is SVG text in the same colour. Swap the paths for the official asset when it lands;
 * the props and the accessible name stay the same.
 *
 * `role="img"` + a label rather than `aria-hidden`: this identifies the payment method, so it
 * carries meaning a screen reader needs.
 */
export type PixLogoProps = {
  /**
   * Rendered height in px; the width follows from the viewBox ratio. Defaults to 26, like
   * `SpeiLogo`. Keep it at or above {@link MIN_LEGIBLE_HEIGHT}.
   */
  height?: number;
};

const VIEW_W = 96;
const VIEW_H = 36;

/** The mark is this many times wider than it is tall. */
const ASPECT = VIEW_W / VIEW_H;

/** Below roughly this height the gap inside the glyph closes and the wordmark blurs. */
const MIN_LEGIBLE_HEIGHT = 16;

export const PixLogo = ({ height = 26 }: PixLogoProps = {}): React.ReactElement => {
  const { t } = useTranslation();
  const h = Math.max(height, MIN_LEGIBLE_HEIGHT);
  return (
    <LogoWrap>
      <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} width={Math.round(h * ASPECT)} height={h} role="img" aria-label={t('Pix')}>
        {/* Glyph: an outer diamond outline linked to a solid inner diamond. */}
        <path
          d="M18 3.5 32.5 18 18 32.5 3.5 18Z"
          fill="none"
          stroke={PIX_BRAND.teal}
          strokeWidth="3.4"
          strokeLinejoin="round"
        />
        <path d="M18 12 24 18 18 24 12 18Z" fill={PIX_BRAND.teal} />
        <text
          x="40"
          y="26"
          fill={PIX_BRAND.teal}
          fontFamily="ui-sans-serif, system-ui, sans-serif"
          fontSize="25"
          fontWeight="700"
          letterSpacing="-0.5"
          aria-hidden
        >
          pix
        </text>
      </svg>
    </LogoWrap>
  );
};

export default PixLogo;
